import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportsContainer, ReportsTitle, SearchBoxCont, ReportsHeader } from './Reports.styles';
import ReportsGroupTable from '../../components/ReportsTable/ReportsGroupTable';
import SearchBox from '../../components/SearchBox/SearchBox';
import Spinner from '../../components/Spinner/Spinner';
import { useFilters } from '../../hooks/useFilters';
import { useReports } from '../../hooks/useReports';
import { IGroupOfReportsItem } from '../../services/reports';
import { IPagingInfo, ISurveyFilters } from '../../services/surveys';
import { SpinnerCont } from '../SurveyContents/SurveyContents.styles';

const defaultPagingInfo = {
  pageNo: 1,
  itemsPerPage: 7,
  totalPageCount: 1,
  totalItemsCount: 1,
  memberCount: 1,
  subordinateCount: 1,
  superordinateCount: 1,
  leaderCount: 1,
  teamReportsCount: 1,
  executiveReportsCount: 1,
};

const Reports = () => {
  const [groupOfReports, setGroupOfReports] = useState<IGroupOfReportsItem[]>([]);
  const [metadata, setMetadata] = useState<IPagingInfo>(defaultPagingInfo);
  const { currentFilters, applyFilters, applyManyNestedFilters } = useFilters();
  const { perPage, pageNo, search: currentSearch } = currentFilters;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { downloadReportGroupList } = useReports();
  const reportsKey = 'reports';

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const getGroupOfReports = async () => {
      setLoading(true);
      try {
        const groupOfReportList = await downloadReportGroupList(signal);
        setGroupOfReports(groupOfReportList.items);
        setMetadata(groupOfReportList.pagingInfo);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load reports', err);
        }
        setGroupOfReports([]);
      } finally {
        setLoading(false);
      }
    };
    getGroupOfReports();

    return () => {
      controller.abort();
    };
  }, [currentSearch, pageNo, perPage]);

  const doSearch = (search: ISurveyFilters) => {
    if (!search.search) {
      const filterArray = groupOfReports.map((item: IGroupOfReportsItem, index: number) => {
        return { id: item.surveyId, filter: { active: index === 0 ? true : null } };
      });
      applyManyNestedFilters(filterArray, reportsKey, search);
    } else {
      if (search.search !== currentSearch) {
        applyFilters({ ...search, reports: null });
      }
    }
  };

  return (
    <ReportsContainer id="ReportsContainer">
      <ReportsHeader id="ReportsHeader">
        <ReportsTitle id="ReportsTitle">{t('reports.reports')}</ReportsTitle>
        <SearchBoxCont>
          <SearchBox
            searchPhrase={doSearch}
            placeholder={t('reports.searchReport')}
            filterName="search"
          />
        </SearchBoxCont>
      </ReportsHeader>
      {loading ? (
        <SpinnerCont id="SpinnerCont" data-testid="spinnerContentsTab">
          <Spinner size="large" />
        </SpinnerCont>
      ) : (
        <ReportsGroupTable items={groupOfReports} metadata={metadata} />
      )}
    </ReportsContainer>
  );
};

export default Reports;
